import { pathLabel, pathRoleOf } from '@/shared/program/path-label';

import type { PlacementModuleView } from './placement-result';
import type { PlacementRecommendation } from '@mio/shared';
import type { ReactNode } from 'react';

function formatSaved(iso: string) {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/** The path in one glance: where it starts, which modules it covers, and why. */
export function PlacementSummary({
  modules,
  recommendation,
  startModule,
  chosenModules,
  saved = null,
  actions,
}: {
  modules: PlacementModuleView[];
  recommendation: PlacementRecommendation;
  startModule: number;
  chosenModules: number[];
  /** Present on the path page once a path has been saved. */
  saved?: { updatedAt: string; adjusted: boolean } | null;
  actions?: ReactNode;
}) {
  const start = modules.find((module) => module.number === startModule);
  const path = { startModule, modules: chosenModules, source: 'adjusted' as const };
  const matchesRecommendation =
    startModule === recommendation.startModule &&
    chosenModules.length === recommendation.modules.length &&
    chosenModules.every((number) => recommendation.modules.includes(number));
  const savedOn = saved ? formatSaved(saved.updatedAt) : null;
  return (
    <section className="path-summary" aria-labelledby="path-summary-title">
      <div className="path-summary-top">
        <span className="path-eyebrow">{saved ? 'Your current path' : 'Recommended for you'}</span>
        {saved ? (
          <span className="path-summary-meta">
            {savedOn ? <span>Saved {savedOn}</span> : null}
            {saved.adjusted ? (
              <span className="path-tag" data-tone="recommended">
                Adjusted by you
              </span>
            ) : null}
          </span>
        ) : null}
      </div>
      <h2 id="path-summary-title">
        {start ? `Start with Module ${start.number} · ${start.title}` : 'Choose a module below'}
      </h2>
      {matchesRecommendation ? (
        <p>{recommendation.summary}</p>
      ) : (
        <>
          <p>
            You changed the recommendation. Your path covers{' '}
            {modules
              .filter((module) => chosenModules.includes(module.number))
              .map((module) => module.title)
              .join(', ')}
            .
          </p>
          <p className="path-summary-note">Recommended: {recommendation.summary}</p>
        </>
      )}
      <ol className="path-strip" aria-label="Modules on your path">
        {modules.map((module) => {
          const role = pathRoleOf(path, module.number);
          return (
            <li key={module.number} className="path-chip" data-role={role}>
              <span className="path-chip-number">{module.number}</span>
              <span>{module.title}</span>
              {role ? <span className="path-chip-role">{pathLabel(role)}</span> : null}
            </li>
          );
        })}
      </ol>
      {actions ? <div className="path-summary-actions">{actions}</div> : null}
    </section>
  );
}
